import type { StudioSession, StudioTurn } from "@szyos/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent, type ReactNode } from "react";
import { api, errorMessage } from "../api";
import { Icon } from "../icons";
import { ErrorNote, useToast } from "./feedback";

/** Verlauf eines Gesprächs im Planungsstudio: Antworten der Beraterin, Rückfragen und Antworten des Owners. */
function Turn({ turn }: { turn: StudioTurn }) {
  if (turn.role === "owner") {
    return (
      <li className="studio-turn is-owner">
        <span className="studio-who">Du</span>
        <p>{turn.text}</p>
      </li>
    );
  }
  const r = turn.reply;
  return (
    <li className="studio-turn is-ai">
      <span className="studio-who">
        <Icon name="bot" /> Beraterin
      </span>
      <p>{r?.summary}</p>
      {r && (r.audience || r.effortEstimate || r.similarProjects?.length) && (
        <dl className="studio-facts">
          {r.audience && (
            <>
              <dt>Zielgruppe</dt>
              <dd>{r.audience}</dd>
            </>
          )}
          {r.effortEstimate && (
            <>
              <dt>Aufwand</dt>
              <dd>{r.effortEstimate}</dd>
            </>
          )}
          {r.similarProjects?.length ? (
            <>
              <dt>Ähnlich</dt>
              <dd>{r.similarProjects.join(", ")}</dd>
            </>
          ) : null}
        </dl>
      )}
      {r?.questions.length ? (
        <>
          <p className="studio-q-head">Rückfragen</p>
          <ol className="studio-questions">
            {r.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ol>
        </>
      ) : null}
    </li>
  );
}

/**
 * Gespräch mit der Beraterin. Zeigt den Verlauf, ein Antwortfeld, solange Rückfragen offen sind, und
 * den Vorschlag (Briefing oder Plan) über `proposalView`.
 */
export function StudioChat({
  session,
  title,
  proposalView,
  onRetry,
  waitText = "Die Beraterin denkt nach. Das dauert meist unter einer Minute.",
}: {
  session: StudioSession;
  title: string;
  proposalView?: ReactNode;
  onRetry?: () => void;
  /** Was gerade passiert, solange die KI arbeitet (etwa: die Prüferin prüft den Plan). */
  waitText?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const answerId = useId();
  const answer = useMutation({
    mutationFn: () => api.answerStudio(session.id, text.trim()),
    onSuccess: async () => {
      setText("");
      setError(null);
      toast("Antwort gesendet. Die Beraterin denkt nach.");
      await queryClient.invalidateQueries({ queryKey: ["studio"] });
    },
    onError: (err) => {
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });
  // Gespeicherte KI-Antwort noch einmal prüfen, ohne neuen Lauf (spart Abo-Kontingent).
  const recheck = useMutation({
    mutationFn: () => api.recheckStudio(session.id),
    onSuccess: async (s) => {
      await queryClient.invalidateQueries({ queryKey: ["studio"] });
      toast(
        s.state === "failed"
          ? "Die Antwort passt weiterhin nicht. Bitte neu versuchen."
          : "Antwort übernommen.",
      );
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return setError("Bitte eine Antwort schreiben.");
    answer.mutate();
  };
  const last = session.turns.at(-1);
  const openQuestions = last?.role === "ai" && (last.reply?.questions.length ?? 0) > 0;

  return (
    <section className="panel studio" aria-labelledby={`studio-${session.id}`}>
      <div className="panel-head">
        <h2 id={`studio-${session.id}`}>{title}</h2>
      </div>
      <ol className="studio-turns">
        {session.turns.map((t, i) => (
          <Turn key={`${t.at}-${i}`} turn={t} />
        ))}
      </ol>
      {session.state === "waiting_ai" && (
        <p className="studio-wait" role="status">
          <span className="pulse-dot" aria-hidden="true" /> {waitText}
        </p>
      )}
      {session.state === "failed" && (
        <div className="studio-failed" role="alert">
          <p>
            <strong>Das hat nicht geklappt:</strong> {session.error}
          </p>
          <div className="idea-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => recheck.mutate()}
              disabled={recheck.isPending}
            >
              <span className="btn-label">
                <Icon name="check" /> Antwort erneut prüfen
              </span>
            </button>
            {onRetry && (
              <button type="button" className="btn" onClick={onRetry}>
                <span className="btn-label">
                  <Icon name="refresh" /> Neu versuchen
                </span>
              </button>
            )}
          </div>
          <p className="muted">
            „Erneut prüfen“ nutzt die vorhandene Antwort und kostet kein Abo-Kontingent.
          </p>
        </div>
      )}
      {proposalView}
      {session.state === "waiting_owner" && (
        <form className="form studio-answer" onSubmit={submit} noValidate>
          <label htmlFor={answerId}>
            {openQuestions ? "Deine Antworten (frei formuliert)" : "Noch etwas ergänzen oder ändern?"}
          </label>
          <textarea id={answerId} rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          <ErrorNote error={error} />
          <button type="submit" className="btn btn--primary" disabled={answer.isPending}>
            <span className="btn-label">
              <Icon name="arrow" /> Antwort senden
            </span>
          </button>
        </form>
      )}
    </section>
  );
}
